/**
 * serverRetry - Barriera di attesa per i server non raggiungibili (middleware e search-anutel).
 *
 * Quando una richiesta fallisce perché il server non risponde, la barriera del server passa a
 * "offline", emette l'evento `server:status` (usato da ServerStatusBanner) e, a intervalli
 * crescenti (5, 10, 20, 30 secondi, poi 30 fissi), controlla `GET <base>/health` con axios nudo
 * (senza interceptor). Alla prima risposta 200 la barriera torna online e libera le richieste in
 * attesa, che api.js rilancia. Solo le letture (GET) vengono ritentate; le scritture falliscono
 * subito come prima, ma fanno comparire la barra.
 */

import axios from 'axios';
import { API_BASE_URL, SEARCH_API_BASE_URL } from '../config/apiConfig';

const INTERVALLI_SECONDI = [5, 10, 20, 30]; // poi 30 fissi
const TIMEOUT_HEALTH_MS = 5000;
const ATTESA_MAX_MS = 180000; // oltre ~3 minuti dal primo errore la richiesta si arrende

const BASE_URL = { middleware: API_BASE_URL, search: SEARCH_API_BASE_URL };

const nuovaBarriera = () => ({
  offline: false,
  controllando: false,
  indice: 0,           // posizione corrente in INTERVALLI_SECONDI
  secondi: 0,          // secondi al prossimo controllo
  timer: null,
  attese: [],          // { resolve, timeout } delle richieste in attesa
});

const barriere = { middleware: nuovaBarriera(), search: nuovaBarriera() };

const emetti = (server) => {
  const b = barriere[server];
  window.dispatchEvent(new CustomEvent('server:status', {
    detail: {
      server,
      offline: b.offline,
      controllando: b.controllando,
      secondiAlProssimoTentativo: b.secondi,
    },
  }));
};

/** Stato corrente di entrambi i server (per il montaggio tardivo della barra). */
export const getStatoServer = () => Object.fromEntries(
  Object.entries(barriere).map(([server, b]) => [server, {
    offline: b.offline,
    controllando: b.controllando,
    secondiAlProssimoTentativo: b.secondi,
  }])
);

const fermaTimer = (b) => {
  if (b.timer) { clearInterval(b.timer); b.timer = null; }
};

const tornaOnline = (server) => {
  const b = barriere[server];
  fermaTimer(b);
  b.offline = false;
  b.controllando = false;
  b.indice = 0;
  b.secondi = 0;
  const attese = b.attese;
  b.attese = [];
  attese.forEach(({ resolve, timeout }) => { clearTimeout(timeout); resolve(); });
  emetti(server);
};

const avviaConteggio = (server) => {
  const b = barriere[server];
  fermaTimer(b);
  b.controllando = false;
  b.secondi = INTERVALLI_SECONDI[Math.min(b.indice, INTERVALLI_SECONDI.length - 1)];
  emetti(server);
  b.timer = setInterval(() => {
    b.secondi -= 1;
    if (b.secondi <= 0) {
      controlla(server); // eslint-disable-line no-use-before-define
    } else {
      emetti(server);
    }
  }, 1000);
};

// Controllo /health: 200 → online, altrimenti passa all'intervallo successivo
const controlla = async (server) => {
  const b = barriere[server];
  if (!b.offline || b.controllando) return;
  fermaTimer(b);
  b.controllando = true;
  b.secondi = 0;
  emetti(server);

  let ok = false;
  try {
    const res = await axios.get(`${BASE_URL[server]}/health`, { timeout: TIMEOUT_HEALTH_MS });
    ok = res.status === 200;
  } catch {
    ok = false;
  }

  if (ok) {
    tornaOnline(server);
  } else {
    b.indice += 1;
    avviaConteggio(server);
  }
};

/** Segnala il server offline (se non lo è già) e avvia il conto alla rovescia. */
export const segnalaOffline = (server) => {
  const b = barriere[server];
  if (b.offline) return;
  b.offline = true;
  b.indice = 0;
  avviaConteggio(server);
};

/** Azzera il conto alla rovescia ed esegue subito il controllo /health. */
export const riprovaOra = (server) => {
  const b = barriere[server];
  if (!b.offline) return;
  controlla(server);
};

/**
 * Errore che conta come "server non raggiungibile": nessuna risposta (rete, server spento, CORS),
 * timeout (ECONNABORTED) oppure 502/503/504. Esclusi 4xx (401/refresh compreso), 500 e richieste annullate.
 */
export const isServerNonRaggiungibile = (error) => {
  if (!error || !error.config || axios.isCancel(error)) return false;
  if (!error.response) return true;
  if (error.code === 'ECONNABORTED') return true;
  return [502, 503, 504].includes(error.response.status);
};

// Attende il ritorno online del server; dopo ATTESA_MAX_MS dal primo errore della richiesta rifiuta
const attendiServer = (server, inizio) => new Promise((resolve, reject) => {
  const b = barriere[server];
  segnalaOffline(server);
  const residuo = Math.max(0, ATTESA_MAX_MS - (Date.now() - inizio));
  const voce = { resolve };
  voce.timeout = setTimeout(() => {
    b.attese = b.attese.filter((v) => v !== voce);
    reject(new Error('Attesa del server scaduta'));
  }, residuo);
  b.attese.push(voce);
});

/**
 * Da chiamare negli interceptor di risposta di api.js. Restituisce null se l'errore non riguarda un
 * server non raggiungibile (l'interceptor prosegue come prima). Altrimenti:
 *  - GET → attende il server e rilancia la richiesta con `istanza`; oltre ~3 minuti rifiuta con l'errore originale;
 *  - altri metodi → nessun nuovo tentativo: segnala la barriera offline e rifiuta subito.
 */
export const gestisciErroreServer = (error, istanza, server) => {
  if (!isServerNonRaggiungibile(error)) return null;

  const config = error.config;
  if ((config.method || '').toLowerCase() !== 'get') {
    segnalaOffline(server);
    return Promise.reject(error);
  }

  config._serverRetryInizio = config._serverRetryInizio || Date.now();
  config._serverRetryErrore = config._serverRetryErrore || error;
  config._serverRetry = (config._serverRetry || 0) + 1;

  return attendiServer(server, config._serverRetryInizio).then(
    () => istanza(config),
    () => Promise.reject(config._serverRetryErrore)
  );
};
